import { type RefObject, useEffect, useState } from "react";

// The page nests its own scroller inside the layout's, and `position: sticky` resolves
// against whichever of them actually scrolls. Observing the viewport instead would
// report the sentinel as still visible while the bar is already stuck.
const scrollParent = (element: HTMLElement): HTMLElement | null => {
  let node = element.parentElement;

  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }

  return null;
};

export const useStuck = (sentinel: RefObject<HTMLElement | null>): boolean => {
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const element = sentinel.current;
    if (!element || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
        setStuck(!entry.isIntersecting && entry.boundingClientRect.top < rootTop);
      },
      { root: scrollParent(element), threshold: 0 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [sentinel]);

  return stuck;
};
